import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import type { ProjectListItem } from '@lawyer/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorAlert } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ProjectDialog } from '../components/projects/ProjectDialog'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'

export function ProjectsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: qk.projects({ search }),
    queryFn: () => api.get<ProjectListItem[]>('/projects', { search: search || undefined }),
  })

  const columns: GridColDef<ProjectListItem>[] = [
    {
      field: 'displayName',
      headerName: 'פרויקט',
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => navigate(`/projects/${params.row.id}`)}
          sx={{ font: 'inherit', color: 'inherit' }}
        >
          {params.row.displayName}
        </Link>
      ),
    },
    { field: 'developerName', headerName: 'חברה יזמית', flex: 1.5, minWidth: 160 },
    { field: 'gush', headerName: 'גוש', width: 90 },
    { field: 'parcel', headerName: 'חלקה', width: 90 },
    { field: 'city', headerName: 'עיר', flex: 1, minWidth: 110 },
    { field: 'street', headerName: 'רחוב', flex: 1, minWidth: 120 },
    { field: 'caseCount', headerName: 'תיקים', width: 90, type: 'number' },
    { field: 'contactCount', headerName: 'אנשי קשר', width: 100, type: 'number' },
  ]

  return (
    <>
      <PageHeader
        title="פרויקטים"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            פרויקט חדש
          </Button>
        }
      />

      <TextField
        label="חיפוש"
        placeholder="שם, חברה יזמית, עיר, גוש או חלקה"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ maxWidth: 380, mb: 2 }}
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}

      <Paper variant="outlined" sx={{ height: 'calc(100vh - 300px)', minHeight: 380 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          loading={isFetching}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 0 }}
        />
      </Paper>

      <ProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(project) => navigate(`/projects/${project.id}`)}
      />
    </>
  )
}
